import React, { useEffect } from 'react';

import { Alert, CircularProgress, Stack, Typography } from '@mui/material';

import { useGetRoomImagesByInternalService } from '~/hooks/apis/accommodation/useGetRoomImagesByInternalService';

import ApiError from '~/types/ApiError';

import ImportableImageGrid from './ImportableImageGrid';

interface Props {
  roomId: string;
  roomName: string;
  open: boolean;
  existingImageIdSet: Set<string>;
  selectedImageIds: Array<string>;
  onToggleSelected: (imageId: string) => void;
  onImagesLoaded: (images: Array<{ imageId: string; title: string | null }>) => void;
}

export default function ImportableRoomImages({
  roomId,
  roomName,
  open,
  existingImageIdSet,
  selectedImageIds,
  onToggleSelected,
  onImagesLoaded,
}: Props) {
  // roomId is already the svc-reservation room id, so no mapping lookup is needed.
  const { data, isLoading, isError, error } = useGetRoomImagesByInternalService({
    internalService: 'reservation',
    internalServiceId: roomId,
    enabled: open,
  });
  const images = data?.images ?? [];

  useEffect(() => {
    onImagesLoaded(images);
    // onImagesLoaded is a parent setState; only images should retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (isError) {
    const isNotMapped = error instanceof ApiError && error.status === 404;
    return (
      <Alert severity="error">
        {isNotMapped
          ? `${roomName} has no approved mapping in accommodation.`
          : `Failed to load images for ${roomName}: ${error instanceof Error ? error.message : 'Unknown error'}`}
      </Alert>
    );
  }

  if (images.length === 0) {
    return <Typography color="text.secondary">No images found for {roomName}.</Typography>;
  }

  return (
    <ImportableImageGrid
      images={images}
      existingImageIdSet={existingImageIdSet}
      selectedImageIds={selectedImageIds}
      onToggleSelected={onToggleSelected}
    />
  );
}
